import { appIntl } from '@nxsys/helpers/Common';
import {
  PaginationType,
} from '@nxsys/types/models/Common';
import { showToast } from '@nxsys/utils/toast';
import { createSlice } from '@reduxjs/toolkit';
import { prospectActs } from '../action';
import { documentAct } from '@nxsys/store/slices/document/action';
import { AgencyProspect, Candidate, GetAgencyProspectListParams, GetCandidateListParams, GetProspectListParams, Prospect } from '../types';

export interface RPNState {
  candidateList: {
    loading: boolean;
    list: Candidate[];
    pagination: PaginationType;
    queryParams: GetCandidateListParams | null;
  };
  agencyList: {
    loading: boolean;
    list: AgencyProspect[];
    pagination: PaginationType;
    queryParams: GetAgencyProspectListParams | null;
  };
  prospectList: {
    loading: boolean;
    list: Prospect[];
    pagination: PaginationType;
    queryParams: GetProspectListParams | null;
  };
  countProspectPending: number;
  countDocumentIDPending: number;
}

const initialState: RPNState = {
  candidateList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  prospectList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  countProspectPending: 0,
  countDocumentIDPending: 0,
};

const prospectSlice = createSlice({
  name: 'prospect',
  initialState,
  reducers: {
    setProspectPendingCount(state, action: { payload: number }) {
      state.countProspectPending = action.payload;
    },
    setDocumentIDPendingCount(state, action: { payload: number }) {
      state.countDocumentIDPending = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Prospect Pending Count
    builder.addCase(prospectActs.getProspectPendingCount.fulfilled, (state, action) => {
      state.countProspectPending = action.payload.countProspectPending;
      state.countDocumentIDPending = action.payload.countDocumentIDPending;
    });

    // Sync document pending count when a document is verified
    builder.addCase(documentAct.verifyDocument.fulfilled, (state, action) => {
      const count = (action.payload as any)?.countDocumentIDPending;
      if (typeof count === 'number') {
        state.countDocumentIDPending = count;
      }
    });

    // Get Candidate Records
    builder.addCase(prospectActs.getCandidateList.pending, (state) => {
      state.candidateList.loading = true;
    });
    builder.addCase(prospectActs.getCandidateList.fulfilled, (state, action) => {
      state.candidateList.loading = false;
      state.candidateList.list = action.payload.data;
      state.candidateList.pagination = action.payload.pagination;
      state.candidateList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(prospectActs.getCandidateList.rejected, (state, action) => {
      state.candidateList.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'prospect.candidate.getError' }),
      });
    });

    // Create Candidate
    builder.addCase(prospectActs.createCandidate.pending, () => {
    });
    builder.addCase(prospectActs.createCandidate.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'prospect.candidate.createSuccess' }),
      });
    });
    builder.addCase(prospectActs.createCandidate.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.candidate.createError' }),
      });
    });

    // Update Candidate By Id
    builder.addCase(prospectActs.updateCandidateById.pending, () => {
    });
    builder.addCase(prospectActs.updateCandidateById.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'prospect.candidate.updateSuccess' }),
      });
    });
    builder.addCase(prospectActs.updateCandidateById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.candidate.updateError' }),
      });
    });

    // Progress Onboarding By Ids
    builder.addCase(prospectActs.progressOnboardingByIds.pending, () => {
    });
    builder.addCase(prospectActs.progressOnboardingByIds.fulfilled, (state, action) => {
      console.info(state)
      const { success, successMesg, warning, warningMesg } = action.payload;
      showToast({
        type: success ? 'success' : 'warning',
        message: success ? successMesg : warning ? warningMesg : appIntl().formatMessage({ id: 'prospect.candidate.progressOnboardingSuccess' }),
      });
    });
    builder.addCase(prospectActs.progressOnboardingByIds.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.candidate.progressOnboardingError' }),
      });
    });

    // Progress Registration By Ids
    builder.addCase(prospectActs.progressRegistrationByIds.pending, () => {
    });
    builder.addCase(prospectActs.progressRegistrationByIds.fulfilled, (state, action) => {
      console.info(state)
      const { success, successMesg, warning, warningMesg } = action.payload;
      showToast({
        type: success ? 'success' : 'warning',
        message: success ? successMesg : warning ? warningMesg : appIntl().formatMessage({ id: 'prospect.candidate.progressRegistrationSuccess' }),
      });
    });
    builder.addCase(prospectActs.progressRegistrationByIds.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.candidate.progressRegistrationError' }),
      });
    });

    // Delete Candidate By Ids
    builder.addCase(prospectActs.deleteCandidateByIds.pending, () => {
    });
    builder.addCase(prospectActs.deleteCandidateByIds.fulfilled, (state, action) => {
      console.info(state)
      const { success } = action.payload;
      showToast({
        type: success ? 'success' : 'error',
        message: success ? appIntl().formatMessage({ id: 'prospect.candidate.deleteSuccess' }) : appIntl().formatMessage({ id: 'prospect.candidate.deleteError' }),
      });
    });
    builder.addCase(prospectActs.deleteCandidateByIds.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.candidate.deleteError' }),
      });
    });

    // Get Candidate Records
    builder.addCase(prospectActs.getAgencyProspectList.pending, (state) => {
      state.agencyList.loading = true;
    });
    builder.addCase(prospectActs.getAgencyProspectList.fulfilled, (state, action) => {
      state.agencyList.loading = false;
      state.agencyList.list = action.payload.data;
      state.agencyList.pagination = action.payload.pagination;
      state.agencyList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(prospectActs.getAgencyProspectList.rejected, (state, action) => {
      state.agencyList.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'prospect.agency.getError' }),
      });
    });

    // Create Agency Prospect
    builder.addCase(prospectActs.createAgencyProspect.pending, () => {
    });
    builder.addCase(prospectActs.createAgencyProspect.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'prospect.agency.createSuccess' }),
      });
    });
    builder.addCase(prospectActs.createAgencyProspect.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.agency.createError' }),
      });
    });

    // Update Agency Prospect
    builder.addCase(prospectActs.updateAgencyProspectById.pending, () => {
    });
    builder.addCase(prospectActs.updateAgencyProspectById.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'prospect.agency.updateSuccess' }),
      });
    });
    builder.addCase(prospectActs.updateAgencyProspectById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.agency.updateError' }),
      });
    });

    // Progress Registration Agency Prospect
    builder.addCase(prospectActs.progressRegistrationAgencyProspectById.pending, () => {
    });
    builder.addCase(prospectActs.progressRegistrationAgencyProspectById.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'prospect.agency.updateSuccess' }),
      });
    });
    builder.addCase(prospectActs.progressRegistrationAgencyProspectById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.agency.updateError' }),
      });
    });

    // Get Onboarding Pending Records
    builder.addCase(prospectActs.getProspectList.pending, (state) => {
      state.prospectList.loading = true;
    });
    builder.addCase(prospectActs.getProspectList.fulfilled, (state, action) => {
      state.prospectList.loading = false;
      state.prospectList.list = action.payload.data;
      state.prospectList.pagination = action.payload.pagination;
      state.prospectList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(prospectActs.getProspectList.rejected, (state, action) => {
      state.prospectList.loading = false;
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.response?.data?.message ||
          appIntl().formatMessage({ id: 'prospect.onboarding.getError' }),
      });
    });

    // Update Agency Prospect
    builder.addCase(prospectActs.updateProspectById.pending, () => {
    });
    builder.addCase(prospectActs.updateProspectById.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'prospect.onboarding.updateSuccess' }),
      });
    });
    builder.addCase(prospectActs.updateProspectById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.onboarding.updateError' }),
      });
    });

    // Resend Prospect
    builder.addCase(prospectActs.resendProspectByIds.pending, () => {
    });
    builder.addCase(prospectActs.resendProspectByIds.fulfilled, (_state, action) => {
      const { success, message } = (action.payload as any) || {};
      showToast({
        type: success ? 'success' : 'error',
        message: message || appIntl().formatMessage({ id: success ? 'prospect.onboarding.resendSuccess' : 'prospect.onboarding.resendError' }),
      });
    });
    builder.addCase(prospectActs.resendProspectByIds.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.onboarding.resendError' }),
      });
    });

    // Progress Prospect
    builder.addCase(prospectActs.progressProspectByIds.pending, () => {
    });
    builder.addCase(prospectActs.progressProspectByIds.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'prospect.onboarding.updateProgressProspectSuccess' }),
      });
    });
    builder.addCase(prospectActs.progressProspectByIds.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.onboarding.updateProgressProspectError' }),
      });
    });

    // Delete Prospect By Id
    builder.addCase(prospectActs.deleteProspectById.pending, () => {
    });
    builder.addCase(prospectActs.deleteProspectById.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'prospect.onboarding.deleteSuccess' }),
      });
    });
    builder.addCase(prospectActs.deleteProspectById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.onboarding.deleteError' }),
      });
    });

    // Progress Prospect
    builder.addCase(prospectActs.deleteProspectByIds.pending, () => {
    });
    builder.addCase(prospectActs.deleteProspectByIds.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'prospect.onboarding.deleteSuccess' }),
      });
    });
    builder.addCase(prospectActs.deleteProspectByIds.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.onboarding.deleteError' }),
      });
    });

    // Get Onboarding by id
    // The employee portal can move the Yoti session on (e.g. to EXPIRED) after
    // the list loaded — copy the state the admin just read onto that row, so
    // its onboarding progress icon matches.
    builder.addCase(prospectActs.getOnboardingByProspectId.fulfilled, (state, action) => {
      const onboarding = action.payload?.data?.onboarding;
      const row = state.prospectList.list.find((item) => item.id === Number(action.meta.arg.id));
      if (!onboarding || !row?.onboarding) return;
      row.onboarding.sessionIdYoti = onboarding.sessionIdYoti;
      row.onboarding.yotiSessionState = onboarding.yotiSessionState;
    });
    builder.addCase(prospectActs.getOnboardingByProspectId.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.onboarding.getDetailError' }),
      });
    });

    // Update Onboarding By Id
    builder.addCase(prospectActs.updateOnboardingById.pending, () => {
    });
    builder.addCase(prospectActs.updateOnboardingById.fulfilled, (_state, action) => {
      if ((action.meta.arg as any)?.hideToast) return;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({
          // Every onboarding endpoint reports back whether that save finished
          // the onboarding — a save that only advances it still says "updated".
          id: (action.payload as any)?.completed
            ? 'prospect.onboarding.completeSuccess'
            : 'prospect.onboarding.updateSuccess',
        }),
      });
    });
    builder.addCase(prospectActs.updateOnboardingById.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.onboarding.updateError' }),
      });
    });

    // Update Onboarding Success
    builder.addCase(prospectActs.updateSuccessOnboardingByProspectId.pending, () => {
    });
    builder.addCase(prospectActs.updateSuccessOnboardingByProspectId.fulfilled, (_state, action) => {
      if ((action.meta.arg as any)?.hideToast) return;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({
          // Every onboarding endpoint reports back whether that save finished
          // the onboarding — a save that only advances it still says "updated".
          id: (action.payload as any)?.completed
            ? 'prospect.onboarding.completeSuccess'
            : 'prospect.onboarding.updateSuccess',
        }),
      });
    });
    builder.addCase(prospectActs.updateSuccessOnboardingByProspectId.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.onboarding.updateError' }),
      });
    });

    // Create Onboarding
    builder.addCase(prospectActs.createOnboardingByProspectId.pending, () => {
    });
    builder.addCase(prospectActs.createOnboardingByProspectId.fulfilled, (_state, action) => {
      if ((action.meta.arg as any)?.hideToast) return;
      showToast({
        type: 'success',
        message: appIntl().formatMessage({
          // Every onboarding endpoint reports back whether that save finished
          // the onboarding — a save that only advances it still says "updated".
          id: (action.payload as any)?.completed
            ? 'prospect.onboarding.completeSuccess'
            : 'prospect.onboarding.updateSuccess',
        }),
      });
    });
    builder.addCase(prospectActs.createOnboardingByProspectId.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.onboarding.updateError' }),
      });
    });

    // Create Prospect
    builder.addCase(prospectActs.createProspect.pending, () => {
    });
    builder.addCase(prospectActs.createProspect.fulfilled, () => {
      showToast({
        type: 'success',
        message: appIntl().formatMessage({ id: 'prospect.createSuccess' }),
      });
    });
    builder.addCase(prospectActs.createProspect.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.createError' }),
      });
    });

    // Delete Agency Prospect By Ids
    builder.addCase(prospectActs.deleteAgencyProspectByIds.pending, () => {
    });
    builder.addCase(prospectActs.deleteAgencyProspectByIds.fulfilled, (state, action) => {
      console.info(state)
      const { success } = action.payload;
      showToast({
        type: success ? 'success' : 'error',
        message: success ? appIntl().formatMessage({ id: 'prospect.agency.deleteSuccess' }) : appIntl().formatMessage({ id: 'prospect.agency.deleteError' }),
      });
    });
    builder.addCase(prospectActs.deleteAgencyProspectByIds.rejected, (state, action) => {
      console.info(state)
      showToast({
        type: 'error',
        message:
          (action.payload as any)?.message ||
          appIntl().formatMessage({ id: 'prospect.agency.deleteError' }),
      });
    });
  },
});

export const prospectReducer = prospectSlice.reducer;
export const prospectActions = {
  ...prospectSlice.actions,
  ...prospectActs,
};
